import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Spin } from 'antd';
import { CloseOutlined, ReloadOutlined, RightOutlined } from '@ant-design/icons';
import { CaptchaApi } from '../../apis/CaptchaApi';
import {
  CaptchaChallengeDTO,
  CaptchaTrackPointDTO,
} from './CaptchaType';
import './TianaiCaptchaModal.css';

interface TianaiCaptchaModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: (token: string) => void;
  primaryColor?: string;
}

const toImageSrc = (raw: string): string => {
  if (!raw) return '';
  if (raw.startsWith('data:')) return raw;
  return `data:image/png;base64,${raw.includes(',') ? raw.split(',').pop() : raw}`;
};

const TianaiCaptchaModal: React.FC<TianaiCaptchaModalProps> = ({
  visible,
  onClose,
  onSuccess,
  primaryColor = '#00BF58',
}) => {
  const [challenge, setChallenge] = useState<CaptchaChallengeDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [boardWidth, setBoardWidth] = useState(0);

  const boardRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const startTimeRef = useRef(0);
  const tracksRef = useRef<CaptchaTrackPointDTO[]>([]);

  const appendTrack = useCallback((type: string, scale: number, currentOffset: number) => {
    if (startTimeRef.current === 0 || scale === 0) return;
    tracksRef.current.push({
      x: currentOffset / scale,
      y: 0,
      t: Date.now() - startTimeRef.current,
      type,
    });
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setChecking(false);
    setError(null);
    setChallenge(null);
    setOffset(0);
    tracksRef.current = [];
    startTimeRef.current = 0;
    const next = await CaptchaApi.genCaptcha();
    setChallenge(next);
    setLoading(false);
    if (!next) {
      setError('验证码加载失败，请重试');
    }
  }, []);

  useEffect(() => {
    if (visible) {
      reload();
    }
  }, [visible, reload]);

  useEffect(() => {
    if (!visible || !boardRef.current) return;
    const el = boardRef.current;
    const update = () => setBoardWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, challenge]);

  const getMetrics = () => {
    if (!challenge || boardWidth <= 0) {
      return { scale: 1, displayHeight: 180, pieceWidth: 60, maxOffset: 0 };
    }
    const scale =
      challenge.backgroundImageWidth === 0
        ? 1
        : boardWidth / challenge.backgroundImageWidth;
    const displayHeight = challenge.backgroundImageHeight * scale;
    const pieceWidth = challenge.templateImageWidth * scale;
    const maxOffset = Math.max(0, boardWidth - pieceWidth);
    return { scale, displayHeight, pieceWidth, maxOffset };
  };

  const submit = async (currentOffset: number, scale: number) => {
    if (!challenge || checking) return;
    setChecking(true);
    const token = await CaptchaApi.checkCaptcha({
      id: challenge.id,
      data: {
        bgImageWidth: challenge.backgroundImageWidth,
        bgImageHeight: challenge.backgroundImageHeight,
        templateImageWidth: challenge.templateImageWidth,
        templateImageHeight: challenge.templateImageHeight,
        startTime: startTimeRef.current,
        stopTime: Date.now(),
        left: Math.round(currentOffset / scale),
        top: 0,
        trackList: [...tracksRef.current],
      },
    });
    if (token) {
      onSuccess(token);
      onClose();
      return;
    }
    setChecking(false);
    setError('验证失败，请重试');
    setOffset(0);
    tracksRef.current = [];
    startTimeRef.current = 0;
    await reload();
  };

  const { scale, displayHeight, pieceWidth, maxOffset } = getMetrics();
  const clampedOffset = Math.min(Math.max(offset, 0), maxOffset);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (checking || loading) return;
    draggingRef.current = true;
    startTimeRef.current = Date.now();
    tracksRef.current = [];
    appendTrack('DOWN', scale, clampedOffset);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current || checking) return;
    const rect = e.currentTarget.parentElement?.getBoundingClientRect();
    if (!rect) return;
    const next = Math.min(Math.max(e.clientX - rect.left - 21, 0), maxOffset);
    setOffset(next);
    appendTrack('MOVE', scale, next);
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current || checking) return;
    draggingRef.current = false;
    const rect = e.currentTarget.parentElement?.getBoundingClientRect();
    const current = rect
      ? Math.min(Math.max(e.clientX - rect.left - 21, 0), maxOffset)
      : clampedOffset;
    setOffset(current);
    appendTrack('UP', scale, current);
    submit(current, scale);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      closable={false}
      maskClosable={false}
      width={360}
      centered
      destroyOnClose
      className="tianai-captcha-modal"
    >
      <div className="tianai-captcha-header">
        <span className="tianai-captcha-title">请完成安全验证</span>
        <div className="tianai-captcha-actions">
          <ReloadOutlined
            onClick={loading ? undefined : reload}
            style={{ cursor: loading ? 'not-allowed' : 'pointer', padding: 8 }}
          />
          <CloseOutlined
            onClick={onClose}
            style={{ cursor: 'pointer', padding: 8 }}
          />
        </div>
      </div>
      {error && <div className="tianai-captcha-error">{error}</div>}
      {loading ? (
        <div className="tianai-captcha-loading">
          <Spin />
        </div>
      ) : (
        <>
          <div
            ref={boardRef}
            className="tianai-captcha-board"
            style={{ height: displayHeight }}
          >
            {challenge?.backgroundImage && (
              <img
                className="tianai-captcha-bg"
                src={toImageSrc(challenge.backgroundImage)}
                alt="captcha background"
                draggable={false}
              />
            )}
            {challenge?.templateImage && (
              <div
                className="tianai-captcha-piece-wrap"
                style={{ left: clampedOffset, width: pieceWidth, height: displayHeight }}
              >
                <img
                  className="tianai-captcha-piece"
                  src={toImageSrc(challenge.templateImage)}
                  alt="captcha piece"
                  draggable={false}
                />
              </div>
            )}
          </div>
          <div className="tianai-captcha-slider">
            <div
              className="tianai-captcha-slider-fill"
              style={{ width: clampedOffset + 42, background: `${primaryColor}2E` }}
            />
            <div className="tianai-captcha-slider-text">向右滑动完成验证</div>
            <div
              className="tianai-captcha-slider-thumb"
              style={{ left: clampedOffset, background: primaryColor }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              {checking ? <Spin size="small" /> : <RightOutlined />}
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default TianaiCaptchaModal;
